/**
 * FRONT-876 review fix: the ONE definition of the graph's entity kinds on
 * the frontend, and how they're labelled/grouped — replacing two
 * independent hand-copied lists that used to live in
 * `features/memory/entity-list.tsx` (the Memory page's type-filter pills)
 * and `components/command-palette.tsx` (the command palette's Memory
 * section, plus its own smaller label map). Mirrors the backend's
 * `ENTITY_TYPES` (`src/graph/types.ts`) — kept as a frontend-local copy
 * since the two apps don't share a build.
 */
export const ENTITY_TYPES = ['person', 'company', 'project', 'event', 'preference'] as const;

export type EntityKind = (typeof ENTITY_TYPES)[number];

/** Curated display label for each canonical kind. */
export const ENTITY_TYPE_LABELS: Record<EntityKind, string> = {
  person: 'Person',
  company: 'Company',
  project: 'Project',
  event: 'Event',
  preference: 'Preference',
};

/**
 * `project` is the one Work container kind (FRONT-2378 retired `routine`) —
 * surfaced by the Work board and the command palette's own Work section, not
 * as generic Memory entries.
 */
export const WORK_CONTAINER_TYPES: readonly EntityKind[] = ['project'];

/**
 * Every entity kind that is NOT a Work container — what the command
 * palette's Memory section (FRONT-876) and any other "everything except
 * projects" view should search/display. Derived from
 * `ENTITY_TYPES`/`WORK_CONTAINER_TYPES`, not hand-copied, so a sixth kind
 * added up there appears (or doesn't) here automatically instead of
 * requiring a second edit that's easy to forget.
 */
export const MEMORY_ENTITY_TYPES: readonly EntityKind[] = ENTITY_TYPES.filter(
  (type) => !(WORK_CONTAINER_TYPES as readonly string[]).includes(type),
);

/** Title-Case a raw string — `"gadget"` → `"Gadget"`, `"smart_device"` →
 *  `"Smart Device"`. Only ever reached as `entityTypeLabel`'s fallback for a
 *  kind outside the curated `ENTITY_TYPE_LABELS` map. */
function titleCase(raw: string): string {
  return raw
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The display label for an entity kind: one of the six curated
 * `ENTITY_TYPE_LABELS`, or a Title-Cased fallback for anything outside that
 * list (a kind the graph doesn't know about yet) — never just echoes the
 * raw lowercase type string back at the user.
 */
export function entityTypeLabel(type: string): string {
  return ENTITY_TYPE_LABELS[type as EntityKind] ?? titleCase(type);
}
